/* eslint-disable max-len */
import React, { useCallback, useState } from 'react';
import { Text, WarningDialog } from '@innovyze/stylovyze';
import { Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';

interface DeleteConfigurationModalProps {
	configurationsToDelete: string[] | 'Multiple';
	open: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	onlyData?: boolean;
}

const DeleteConfigurationDialog = (props: DeleteConfigurationModalProps) => {
	const { t } = useGlobalization();
	const {
		configurationsToDelete,
		open,
		onConfirm,
		onCancel,
		onlyData = false,
	} = props;
	const [checkboxChecked, setCheckboxChecked] = useState(false);

	const getTitle = useCallback((): string => {
		if (configurationsToDelete?.length === 0) {
			return '';
		} else if (configurationsToDelete?.length === 1) {
			if (onlyData)
				return t('Delete data for sensor {{sensorName}}', {
					sensorName: configurationsToDelete[0],
				});
			else
				return t('Delete sensor {{sensorName}}', {
					sensorName: configurationsToDelete[0],
				});
		} else {
			if (onlyData) return t('Delete data for sensors');
			else return t('Delete Sensors');
		}
	}, [configurationsToDelete, onlyData]);

	const getContentText = useCallback((): string => {
		if (configurationsToDelete?.length === 0) {
			return '';
		} else if (configurationsToDelete?.length === 1) {
			if (onlyData) return t('Do you really want to delete this data?');
			else return t('Do you really want to delete this sensor?');
		} else {
			if (onlyData) return t('Do you really want to delete these data?');
			else {
				if (configurationsToDelete === 'Multiple')
					return t('Do you really want to delete multiple sensors?');
				else
					return t(
						'Do you really want to delete these {{sensorCount}} sensors?',
						{
							sensorCount: configurationsToDelete?.length,
						},
					);
			}
		}
	}, [configurationsToDelete, onlyData]);

	const getCheckboxLabel = useCallback((): string => {
		if (configurationsToDelete?.length === 0) {
			return '';
		} else if (configurationsToDelete?.length === 1) {
			if (onlyData)
				return t(
					'I understand that deleting this sensor data may cause certain tools, charts, and alerts not to function correctly.',
				);
			else
				return t(
					'I understand that deleting this sensor and its data may cause certain tools, charts, and alerts not to function correctly.',
				);
		} else {
			if (onlyData)
				return t(
					'I understand that deleting these sensor data may cause certain tools, charts, and alerts not to function correctly.',
				);
			else {
				return t(
					'I understand that deleting these sensors and data may cause certain tools, charts, and alerts not to function correctly.',
				);
			}
		}
	}, [configurationsToDelete, onlyData]);

	const handleOnConfirm = () => {
		onConfirm();
		setCheckboxChecked(false);
	};

	return (
		<WarningDialog
			title={getTitle()}
			open={open}
			confirmText={t('Delete')}
			cancelText={t('Cancel')}
			onConfirm={handleOnConfirm}
			onCancel={onCancel}
			confirmButtonProps={{ disabled: !checkboxChecked }}
			dividers="both">
			<Grid container spacing={4}>
				<Grid
					item
					xs={12}
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						alignItems: 'stretch',
						width: '584px',
						minHeight: '192px',
					}}>
					<Text variant="body1">{getContentText()}</Text>

					<Text variant="body1">
						{t('This action cannot be undone.')}
					</Text>

					{!onlyData ? (
						<Text variant="body1">
							{t(
								`The sensor configuration${
									configurationsToDelete?.length > 1
										? 's'
										: ''
								} and all sensor data will be deleted.`,
							)}
						</Text>
					) : null}

					<br />
					<FormGroup row>
						<FormControlLabel
							control={
								<Checkbox
									checked={checkboxChecked}
									onChange={(
										event: React.ChangeEvent<HTMLInputElement>,
										checked: boolean,
									) => setCheckboxChecked(checked)}
									name="checkedB"
									color="primary"
								/>
							}
							label={getCheckboxLabel()}
						/>
					</FormGroup>
				</Grid>
			</Grid>
		</WarningDialog>
	);
};

export default DeleteConfigurationDialog;
